import { DisplayPrice } from '@wix/wix-ui-premium-purchase';
import classNames from 'classnames';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@wix/design-system';
import { ProductFullPrice } from 'components/ProductFullPrice/ProductFullPrice';
import { taxIncludedTranslationKey } from '../../../../../constants';
import { RootStore, withInjectedRootStore } from 'providers';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { Product } from '../../../Product';
import s from './PriceWithDecimal.scss';

export interface PriceWithDecimalProps {
  rootStore: RootStore;
  product: Product | AdvancedPlansProductModel;
  size?: 'small' | 'tiny';
  className?: string;
}

const PriceWithDecimalComp: React.FC<PriceWithDecimalProps> = ({
  rootStore,
  product,
  size,
  className,
}) => {
  const { t } = useTranslation();
  const {
    appContext: { country, currencyCode, currentPlanDetails },
    pricesIncludeTax,
    currentCycle,
    isChangePlan,
    isJapanesePackagePicker,
    isSunriseV2Enabled,
    hasMonthlyCycle,
  } = rootStore.appStore;
  const cycle = currentPlanDetails?.paymentCycle || currentCycle;
  const fullPrice = isChangePlan
    ? product.getFullPriceByCycle(cycle)
    : product.getYearlyFullPrice();
  const actualPrice = isChangePlan
    ? product.getActualPriceByCycle(cycle)
    : product.getFirstCycleYearlyMonthly() || product.getYearlyActualPrice();
  const isDayfulFreeProduct = product.getName() === 'Free';

  const _fullPrice = isDayfulFreeProduct ? 0 : fullPrice;
  const _actualPrice = isDayfulFreeProduct ? 0 : actualPrice;
  const isMediumTableViewMode = rootStore.appStore.isMediumSizeTableView;

  return (
    <div
      className={classNames(s.priceContainer, className, {
        [s.priceWithDecimalMediumView]: isMediumTableViewMode,
        [s.spacedPrice]: isSunriseV2Enabled,
      })}
    >
      <DisplayPrice
        price={_actualPrice}
        weight='bold'
        theme={size}
        fontUpgrade={true}
        cycle={t('product_price.month')}
        currencyCode={currencyCode}
        hideZeroDecimal={true}
        dataHook='display-price'
        styling={{
          price: classNames(s.price, { [s.japan]: isJapanesePackagePicker }),
          currencySymbol: classNames(s.currencySymbol, {
            [s.japan]: isJapanesePackagePicker,
          }),
          decimalAndCycle: classNames(s.decimalAndCycle, {
            [s.japan]: isJapanesePackagePicker,
          }),
        }}
      />
      <ProductFullPrice
        yearlyActualPrice={_actualPrice}
        fullPrice={_fullPrice}
      />
      {pricesIncludeTax && (
        <div className={s.gst} data-hook='tax-note'>
          <Text size='tiny'>{t(taxIncludedTranslationKey[country])}</Text>
        </div>
      )}
      {!hasMonthlyCycle && (
        <div className={s.gst} data-hook='annually-note'>
          <Text size='tiny'>{t('product.billed_annually')}</Text>
        </div>
      )}
    </div>
  );
};

export const PriceWithDecimal = withInjectedRootStore(PriceWithDecimalComp);
